import { useCallback, useEffect, useState } from "react";

import { WalletDebitsService } from "../services/walletDebits.service";
import type { WalletDebit } from "../types/walletDebits.types";

interface DebitRead {
  id: string;
  debit: WalletDebit | null;
}

/**
 * One debit, for the statement detail. A failed read (staff off the allowlist answer 403) leaves `debit` null
 * and the caller shows the leg alone. `replace` takes the answer of an upload, which carries the new proof.
 */
export function useWalletDebit(debitId: string | null): {
  debit: WalletDebit | null;
  isLoading: boolean;
  replace: (debit: WalletDebit) => void;
} {
  const [read, setRead] = useState<DebitRead | null>(null);

  useEffect(() => {
    if (!debitId) return;
    let cancelled = false;
    WalletDebitsService.get(debitId)
      .then((debit) => {
        if (!cancelled) setRead({ id: debitId, debit });
      })
      .catch(() => {
        if (!cancelled) setRead({ id: debitId, debit: null });
      });
    return () => {
      cancelled = true;
    };
  }, [debitId]);

  const replace = useCallback((debit: WalletDebit) => setRead({ id: debit.id, debit }), []);

  if (!debitId) return { debit: null, isLoading: false, replace };
  if (read?.id !== debitId) return { debit: null, isLoading: true, replace };
  return { debit: read.debit, isLoading: false, replace };
}
